import React, { Component } from 'react'
// redux
import {
  InjectedFormProps,
  reduxForm,
  SubmissionError,
  change,
  WrappedFieldArrayProps
} from 'redux-form'
import { compose, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { recruitersEmails } from 'domain/manager/selectors'
import { inviteRecruitersPromiseCreator, inviteRecruiters } from 'domain/manager/recruiters'
import { bindPromiseCreators, PromiseCreator } from 'redux-saga-routines'
// components
import { OldButton } from 'components/Button'
import { BorderColor, FieldInput } from 'components/Form/Fields/FieldInput'
import { FieldCustomArray } from 'components/Form/Fields/FieldCustomArray'
import Input from 'components/Form/Input'
// types
import { ButtonTypes, ComposeType } from 'types/common'
import { Classes } from 'jss'
import { InvitedRecruitersEmailsType } from 'types/managerTypes'
import { List } from 'immutable'
import { StateInterface } from 'types/state'
// styles
import { sheet } from './sheet'
import injectSheet from 'react-jss'
// utils
import validateEmails from './validation'
// icons
import PlusBlue from 'components/svgComponent/PlusBlue'
import Minus from 'components/svgComponent/Minus'

interface ExternalProps {
  toggleModal: () => void
}

interface OwnProps {
  classes: Classes
  inviteRecruiters: (data?: Array<{ email: string }>) => void
  recruiters: List<InvitedRecruitersEmailsType>
  inviteRecruitersPromiseCreator: PromiseCreator
  dispatch: Dispatch
}

interface EmailRowValue {
  email: string
  available?: boolean
  sent?: boolean
}

interface EmailRowError {
  email?: string
}

interface FormValues {
  emails?: EmailRowValue[]
}

type Props = OwnProps & ExternalProps & InjectedFormProps<FormValues, OwnProps & ExternalProps>

interface State {
  emailCount: number
  formError: string
}

type CheckEmailType = (i: number) => BorderColor | undefined

export const submitInviteRecruiters = async (
  values: FormValues,
  props: OwnProps & ExternalProps
) => {
  const rows = values.emails || []
  if (!rows.length) return

  // відправляємо тільки ті, які ще не sent
  const sendIndexes: number[] = []
  const payload = rows
    .map((r, i) => {
      const email = r && r.email ? String(r.email).trim() : ''
      const isSent = !!(r && r.sent)
      if (!email || isSent) return null
      sendIndexes.push(i)
      return { email }
    })
    .filter(Boolean) as Array<{ email: string }>

  if (!payload.length) {
    // нема що відправляти
    props.toggleModal()
    return
  }

  try {
    const resp: any = await props.inviteRecruitersPromiseCreator(payload, props.dispatch)
    const result = resp && resp.toJS ? resp.toJS() : resp

    const responseEmails =
      (result && result.emails) ||
      (result && result.data && result.data.emails) ||
      (result && result.data && result.data.data && result.data.data.emails)

    const updatedRows = rows.slice()
    const fieldErrors: Array<EmailRowError | undefined> = []

    if (Array.isArray(responseEmails)) {
      responseEmails.forEach((e: any, j: number) => {
        const originalIndex = sendIndexes[j]
        if (originalIndex == null) return

        const available = !!(e && e.available === true)

        updatedRows[originalIndex] = {
          ...updatedRows[originalIndex],
          email: updatedRows[originalIndex] && updatedRows[originalIndex].email,
          available: e && e.available,
          sent: available
        }

        if (e && e.available === false) {
          fieldErrors[originalIndex] = {
            email: 'This recruiter cannot be invited (not available).'
          }
        }
      })
    }

    // ✅ оновлюємо значення форми, щоб UI одразу перемалювався (sent/available)
    props.dispatch(change('invite-recruiters', 'emails', updatedRows as any))

    props.inviteRecruiters()

    const hasUnavailable = fieldErrors.some(Boolean)
    if (hasUnavailable) {
      throw new SubmissionError({
        emails: fieldErrors,
        _error: 'Some emails are not available. Please fix them and try again.'
      } as any)
    }

    // All good → close modal
    props.toggleModal()
  } catch (err) {
    if (err && (err instanceof SubmissionError || err.name === 'SubmissionError')) {
      throw err
    }
    const failurePayload: any = err
    const errorText =
      failurePayload &&
      failurePayload.err &&
      failurePayload.err.response &&
      failurePayload.err.response.data &&
      ((failurePayload.err.response.data.errors &&
        failurePayload.err.response.data.errors.length > 0 &&
        failurePayload.err.response.data.errors[0].message) ||
        failurePayload.err.response.data.message)

    throw new SubmissionError({ _error: errorText || 'Something went wrong!' })
  }
}

class InviteRecruiters extends Component<Props, State> {
  state = {
    emailCount: 1,
    formError: ''
  }

  inputArray = (
    props: WrappedFieldArrayProps<{ email: string }> & {
      recruiters: List<InvitedRecruitersEmailsType>
    }
  ) => {
    const { fields } = props
    const { classes } = this.props
    const addField = () => {
      fields.push({ email: '' })
    }
    if (fields.length < 1) fields.push({ email: '' })
    const isDisabled = this.props.submitting

    return (
      <div className={classes.arrayFields}>
        <p className={classes.title}>
          Enter email address <span>(1 or more)</span>
        </p>
        {fields.map((item, i) => {
          const rowValue: any = fields.get(i) || {}
          const isSent = !!rowValue.sent
          const remove = () => {
            if (!isDisabled) fields.remove(i)
          }
          const available = rowValue.available
          const borderColor =
            available === true
              ? BorderColor.Success
              : available === false
              ? BorderColor.ErrorEmail
              : undefined

          return (
            <div key={i} className={classes.field}>
              <FieldInput
                name={`${item}.email`}
                type="text"
                component={Input}
                placeholder="email@example.co"
                borderColor={borderColor}
              />
              {fields.length > 1 && <Minus onClick={remove} />}
            </div>
          )
        })}
        <OldButton
          label="add email address"
          type={ButtonTypes.BlueButton}
          Icon={PlusBlue}
          disabled={isDisabled}
          onClick={() => !isDisabled && addField()}
        />
      </div>
    )
  }

  render() {
    const { classes, handleSubmit, recruiters, error, submitting } = this.props

    return (
      <form onSubmit={handleSubmit}>
        <div className={classes.root}>
          {error && <p className={classes.error}>{error}</p>}
          <p>
            Only invited recruiters can work with you. To invite – type email of recruiter(s) you
            want to invite to the system. Once they receive mail with a link to Skilent they will
            able to register here and apply to jobs. You will see registered Candidates that you
            have invited in your Dashboard.
          </p>
          <FieldCustomArray name="emails" component={this.inputArray} recruiters={recruiters} />
        </div>
      </form>
    )
  }
}

export default compose<ComposeType<Props, ExternalProps>>(
  connect(
    (state: StateInterface) => ({
      recruiters: recruitersEmails(state).get('emails')
    }),
    dispatch => ({
      dispatch,
      inviteRecruiters: () => dispatch(inviteRecruiters()),
      ...bindPromiseCreators(
        {
          inviteRecruitersPromiseCreator
        },
        dispatch
      )
    })
  ),
  reduxForm<FormValues, OwnProps & ExternalProps>({
    form: 'invite-recruiters',
    validate: validateEmails,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    onSubmit: (values, dispatch, props: OwnProps & ExternalProps) =>
      submitInviteRecruiters(values, props)
  }),
  injectSheet(sheet)
)(InviteRecruiters)
